/* Create/Edit-modal + delete-bevestiging voor kandidaten. */
import React, { useState } from "react";
import i18n from "i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Save, Trash2, X } from "lucide-react";

export function CandidateFormModal({ candidate, pools = [], onClose, onSaved }) {
    const isEdit = !!candidate;
    const [form, setForm] = useState(() => ({
        name: candidate?.name || "",
        title: candidate?.title || "",
        current_company: candidate?.current_company || "",
        current_role: candidate?.current_role || "",
        seniority: candidate?.seniority || "medior",
        location: candidate?.location || "",
        email: candidate?.email || "",
        phone: candidate?.phone || "",
        linkedin_url: candidate?.linkedin_url || "",
        github_url: candidate?.github_url || "",
        skills: (candidate?.skills || []).join(", "),
        talent_pool: candidate?.talent_pool || "external",
        notes: candidate?.notes || "",
    }));
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    // Pool-opties: alle workspace-pools (system + custom), fallback op de 2 system pools.
    const poolOptions = pools.length > 0
        ? pools.map((p) => ({ v: p.key, l: p.name }))
        : [{ v: "external", l: i18n.t("search.pool_external") }, { v: "cronos", l: i18n.t("search.pool_cronos") }];
    const save = async () => {
        if (!form.name.trim()) return toast.error(i18n.t("search.name_required"));
        setSaving(true);
        try {
            const payload = {
                ...form,
                skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
            };
            if (isEdit) {
                await api.patch(`/candidates/${candidate.id}`, payload);
                toast.success(i18n.t("search.cand_updated"));
            } else {
                await api.post("/candidates", payload);
                toast.success(i18n.t("search.cand_created"));
            }
            onSaved?.();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm p-4 md:p-10 overflow-y-auto" onClick={onClose} data-testid="candidate-form-modal">
            <div className="max-w-2xl mx-auto rounded-2xl border border-white/10 bg-[var(--jr-surface)] shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="p-5 border-b border-white/8 flex items-center justify-between">
                    <h3 className="font-display text-xl tracking-tight text-white">
                        {isEdit ? i18n.t("search.cand_edit") : i18n.t("search.cand_new")}
                    </h3>
                    <button onClick={onClose} className="text-[var(--jr-text-3)] hover:text-white" data-testid="candidate-form-close"><X size={18}/></button>
                </div>
                <div className="p-5 grid gap-4 sm:grid-cols-2">
                    <FormInput label={i18n.t("search.lbl_name")} value={form.name} onChange={(v) => set("name", v)} testid="cf-name"/>
                    <FormInput label={i18n.t("search.lbl_title")} value={form.title} onChange={(v) => set("title", v)} testid="cf-title"/>
                    <FormInput label={i18n.t("search.lbl_company")} value={form.current_company} onChange={(v) => set("current_company", v)} testid="cf-company"/>
                    <FormInput label={i18n.t("search.lbl_role")} value={form.current_role} onChange={(v) => set("current_role", v)} testid="cf-role"/>
                    <FormSelect label="Seniority" value={form.seniority} onChange={(v) => set("seniority", v)} testid="cf-seniority"
                        options={[
                            { v: "junior", l: "Junior" }, { v: "medior", l: "Medior" },
                            { v: "senior", l: "Senior" }, { v: "lead", l: "Lead" },
                            { v: "manager", l: "Manager" }, { v: "director", l: "Director" },
                        ]}/>
                    <FormSelect label="Pool" value={form.talent_pool} onChange={(v) => set("talent_pool", v)} testid="cf-pool"
                        options={poolOptions}/>
                    <FormInput label={i18n.t("search.lbl_location")} value={form.location} onChange={(v) => set("location", v)} testid="cf-location"/>
                    <FormInput label="Email" value={form.email} onChange={(v) => set("email", v)} testid="cf-email"/>
                    <FormInput label={i18n.t("search.lbl_phone")} value={form.phone} onChange={(v) => set("phone", v)} testid="cf-phone"/>
                    <FormInput label="LinkedIn URL" value={form.linkedin_url} onChange={(v) => set("linkedin_url", v)} testid="cf-linkedin"/>
                    <FormInput label="GitHub URL" value={form.github_url} onChange={(v) => set("github_url", v)} testid="cf-github"/>
                    <div className="sm:col-span-2">
                        <FormInput label="Skills (comma-separated)" value={form.skills} onChange={(v) => set("skills", v)} testid="cf-skills"
                            placeholder="React, Node.js, PostgreSQL"/>
                    </div>
                    <div className="sm:col-span-2">
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{i18n.t("search.lbl_notes")}</label>
                        <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3}
                            data-testid="cf-notes"
                            className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]"/>
                    </div>
                </div>
                <div className="p-5 border-t border-white/8 flex justify-end gap-2">
                    <button onClick={onClose} data-testid="cf-cancel"
                        className="px-4 py-2 rounded-full border border-white/10 text-sm text-[var(--jr-text-2)] hover:bg-white/5">
                        {i18n.t("common.cancel")}
                    </button>
                    <button onClick={save} disabled={saving} data-testid="cf-save"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[var(--jr-brand)] text-white text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                        {saving ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>}
                        {isEdit ? i18n.t("common.save") : i18n.t("common.create")}
                    </button>
                </div>
            </div>
        </div>
    );
}

function FormInput({ label, value, onChange, testid, placeholder }) {
    return (
        <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}</label>
            <input value={value || ""} onChange={(e) => onChange(e.target.value)}
                data-testid={testid} placeholder={placeholder || ""}
                className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]"/>
        </div>
    );
}

function FormSelect({ label, value, onChange, testid, options }) {
    return (
        <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}</label>
            <select value={value || ""} onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]">
                {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
        </div>
    );
}

export function DeleteConfirmModal({ candidate, onCancel, onConfirmed }) {
    const [busy, setBusy] = useState(false);
    const confirm = async () => {
        setBusy(true);
        try {
            await api.delete(`/candidates/${candidate.id}`);
            toast.success(i18n.t("search.cand_deleted", { name: candidate.name }));
            onConfirmed?.();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("search.delete_failed")); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm p-4 grid place-items-center" onClick={onCancel} data-testid="candidate-delete-modal">
            <div className="max-w-sm w-full rounded-2xl border border-red-500/30 bg-[var(--jr-surface)] shadow-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="p-5 flex items-center gap-3 border-b border-white/8">
                    <div className="w-9 h-9 rounded-full bg-red-500/15 border border-red-500/40 grid place-items-center">
                        <AlertTriangle size={16} className="text-red-400"/>
                    </div>
                    <div className="min-w-0">
                        <div className="font-medium text-white">{i18n.t("search.delete_cand_title")}</div>
                        <div className="text-xs text-[var(--jr-text-2)] mt-0.5 truncate">
                            <strong className="text-white">{candidate.name}</strong> · {candidate.title || ""}
                        </div>
                    </div>
                </div>
                <div className="p-5 text-xs text-[var(--jr-text-2)] leading-relaxed">
                    {i18n.t("search.delete_cand_body")}
                </div>
                <div className="p-5 pt-0 flex justify-end gap-2">
                    <button onClick={onCancel} data-testid="cd-cancel"
                        className="px-4 py-2 rounded-full border border-white/10 text-sm text-[var(--jr-text-2)] hover:bg-white/5">
                        {i18n.t("common.cancel")}
                    </button>
                    <button onClick={confirm} disabled={busy} data-testid="cd-confirm"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-600 disabled:opacity-50">
                        {busy ? <Loader2 className="animate-spin" size={14}/> : <Trash2 size={14}/>}
                        {i18n.t("common.delete")}
                    </button>
                </div>
            </div>
        </div>
    );
}
